import React from 'react';
import { Box } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { useMutation } from 'react-query';
import axios, { endPoints } from 'api/request';
import throttle from 'lodash/throttle';
import { useRecoilState } from 'recoil';
import Select from 'components/select';
import { orderPayload } from 'pages/provisioning/virtual-service/store';

const filter = createFilterOptions();
const ApplicationService = () => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [order, setOrder] = useRecoilState(orderPayload);
    const [value, setValue] = React.useState(null);
    const [ccInputValue, setCCInputValue] = React.useState(order?.applicationService || '');
    const [options, setOptions] = React.useState([]);

    const bCiPostApi = useMutation((payload) => axios.post(`${endPoints.riServices.COMMON.businessCiDetails}`, payload));

    const setAppService = (ciService) => {
        setOrder({
            ...order,
            applicationService: ciService?.label,
            applicationServiceDetails: ciService,
        });
    };
    const resetOptions = () => {
        setOptions([]);
    };

    const fetch = React.useMemo(
        () =>
            throttle((inputValue) => {
                bCiPostApi?.mutate({
                    businessApplication: null,
                    applicationService: inputValue,
                });
            }, 1000),
        []
    );

    React.useEffect(() => {
        if (ccInputValue === '') {
            resetOptions();
        }
        if (ccInputValue && ccInputValue !== '' && ccInputValue?.length > 2) fetch(ccInputValue);
    }, [ccInputValue, fetch]);

    React.useEffect(() => {
        if (bCiPostApi?.isSuccess) {
            if (bCiPostApi?.data?.data?.applications) {
                const { applicationServices = [] } = bCiPostApi?.data?.data?.applications?.[0] || {};
                const combinedValue = applicationServices?.map((app) => ({
                    label: `${app?.serviceName}`,
                    value: `${app?.serviceName}`,
                    data: bCiPostApi?.data?.data?.applications?.[0],
                }));
                setOptions(combinedValue);
            }
        }
    }, [bCiPostApi?.isSuccess]);

    React.useEffect(() => {
        if (value === null) {
            resetOptions();
        }
        if (value !== null && value?.value) {
            setAppService(value);
        }
    }, [value]);

    React.useEffect(() => {
        if (order?.aksNamespace) {
            resetOptions();
        }
    }, [order?.aksNamespace]);

    React.useEffect(() => {
        if (!order?.applicationService && order?.isTouched) setIsTouched(true);
    }, [order?.applicationService, order?.isTouched]);

    return (
        <Box display="flex" alignItems="center" gap={1}>
            <Select
                dataTestId="app-service-autocomplete"
                key="application Service"
                loading={bCiPostApi?.isLoading}
                label="Application Service CI *"
                placeholder="Type to search"
                value={order?.applicationService || null}
                onInputChange={(event, newInputValue) => {
                    if (newInputValue && newInputValue !== '') setCCInputValue(newInputValue || '');
                }}
                onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                        setValue({
                            label: newValue,
                        });
                    } else if (newValue && newValue.inputValue) {
                        setValue({
                            label: newValue.inputValue,
                        });
                    } else {
                        setValue(newValue);
                    }
                }}
                onKeyUp={(event) => {
                    if ((event.key === 'Backspace' || event.key === 'Delete') && (!event.target.value || event.target.value === '')) {
                        resetOptions();
                        setOrder({
                            ...order,
                            applicationService: null,
                            applicationServiceDetails: null,
                        });
                    }
                }}
                filterOptions={(optionValues, params) => filter(optionValues, params)}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                id="free-solo-with-text-demo"
                options={options}
                getOptionLabel={(option) => {
                    if (typeof option === 'string') {
                        return option;
                    }
                    if (option.inputValue) {
                        return option.inputValue;
                    }
                    return option.label;
                }}
                renderOption={(props, option) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <li {...props}>{option.label}</li>
                )}
                freeSolo
                isRequired={isTouched}
            />
        </Box>
    );
};

export default React.memo(ApplicationService);
